/** @format */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { aiService } from '../utils/aiService';

// Types for practice content
export interface PracticeContent {
  id: string;
  type: 'quiz' | 'likely_questions' | 'flashcards';
  title: string;
  content: any;
  source: 'text' | 'pdf';
  sourceContent: string;
  contentLength: number;
  createdAt: Date;
  userId: string;
  isBookmarked?: boolean;
  assessmentScore?: number;
  assessmentDate?: Date;
  metadata?: {
    difficulty?: string;
    estimatedTime?: number;
    tags?: string[];
  };
}

export interface PracticeHistory {
  id: string;
  title: string;
  type: 'quiz' | 'likely_questions' | 'flashcards';
  date: Date;
  score?: number;
  status: 'completed' | 'saved' | 'in_progress';
  userId: string;
}

export interface PracticeStats {
  totalGenerated: number;
  totalQuizzes: number;
  totalQuestions: number;
  totalFlashcards: number;
  averageScore: number;
  totalAssessments: number;
}

export const usePracticeContent = (userId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [practiceContent, setPracticeContent] = useState<PracticeContent[]>([]);
  const [practiceHistory, setPracticeHistory] = useState<PracticeHistory[]>([]);
  const [practiceStats, setPracticeStats] = useState<PracticeStats>({
    totalGenerated: 0,
    totalQuizzes: 0,
    totalQuestions: 0,
    totalFlashcards: 0,
    averageScore: 0,
    totalAssessments: 0
  });

  // Cache keys and TTLs
  const getContentCacheKey = (uid: string) => `practice_content_cache_v1_${uid}`;
  const getHistoryCacheKey = (uid: string) => `practice_history_cache_v1_${uid}`;
  const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  // Firestore collections (memoized to keep stable references)
  const practiceContentCollection = useMemo(() => collection(db, 'practiceContent'), []);
  const practiceHistoryCollection = useMemo(() => collection(db, 'practiceHistory'), []);

  // Load practice content from Firestore
  const loadPracticeContent = useCallback(async () => {
    if (!userId || userId.trim() === '') {
      setLoading(false); // Ensure loading is false when no user
      return;
    }

    try {
      let seededFromCache = false;
      let isCacheFresh = false;

      // Seed from cache (SWR) if available
      try {
        const cachedRaw = localStorage.getItem(getContentCacheKey(userId));
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw) as { data: PracticeContent[]; updatedAt: number };
          if (Array.isArray(cached?.data)) {
            // Rehydrate dates
            const hydrated = cached.data.map((c: any) => ({
              ...c,
              createdAt: c.createdAt ? new Date(c.createdAt) : new Date(),
              assessmentDate: c.assessmentDate ? new Date(c.assessmentDate) : undefined,
            })) as PracticeContent[];
            setPracticeContent(hydrated);
            seededFromCache = true;
            isCacheFresh = !!(cached.updatedAt && (Date.now() - cached.updatedAt) < CACHE_TTL_MS);
          }
        }
      } catch (_) {
        // ignore cache errors
      }

      if (isCacheFresh) {
        // Skip network fetch if cache is fresh
        return;
      }

      if (!seededFromCache) {
        setLoading(true);
      }
      const q = query(
        practiceContentCollection,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      
      const querySnapshot = await getDocs(q);
      const content: PracticeContent[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        content.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          assessmentDate: data.assessmentDate?.toDate() || undefined
        } as PracticeContent);
      });
      
      setPracticeContent(content);

      // Write to cache (serialize dates)
      try {
        const serializable = content.map((c) => ({
          ...c,
          createdAt: c.createdAt?.toISOString?.() || c.createdAt,
          assessmentDate: c.assessmentDate ? c.assessmentDate.toISOString() : undefined,
        }));
        localStorage.setItem(getContentCacheKey(userId), JSON.stringify({ data: serializable, updatedAt: Date.now() }));
      } catch (_) {
        // ignore cache write errors
      }
    } catch (err) {
      console.error('Error loading practice content:', err);
      setError('Failed to load practice content');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Load practice history from Firestore
  const loadPracticeHistory = useCallback(async () => {
    if (!userId) return;

    try {
      let seededFromCache = false;
      let isCacheFresh = false;

      // Seed from cache (SWR)
      try {
        const cachedRaw = localStorage.getItem(getHistoryCacheKey(userId));
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw) as { data: PracticeHistory[]; updatedAt: number };
          if (Array.isArray(cached?.data)) {
            const hydrated = cached.data.map((h: any) => ({
              ...h,
              date: h.date ? new Date(h.date) : new Date(),
            })) as PracticeHistory[];
            // Deduplicate and sort
            const uniqueById = new Map<string, PracticeHistory>();
            hydrated.forEach((item) => uniqueById.set(item.id, item));
            const deduped = Array.from(uniqueById.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
            setPracticeHistory(deduped);
            seededFromCache = true;
            isCacheFresh = !!(cached.updatedAt && (Date.now() - cached.updatedAt) < CACHE_TTL_MS);
          }
        }
      } catch (_) {
        // ignore cache errors
      }

      if (isCacheFresh) {
        return;
      }

      const q = query(
        practiceHistoryCollection,
        where('userId', '==', userId),
        orderBy('date', 'desc'),
        limit(100)
      );
      
      const querySnapshot = await getDocs(q);
      const history: PracticeHistory[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        history.push({
          id: doc.id,
          ...data,
          date: data.date?.toDate() || new Date()
        } as PracticeHistory);
      });
      
      // Deduplicate by id (in case of transient duplicates) and ensure stable sort
      const uniqueById = new Map<string, PracticeHistory>();
      history.forEach((item) => {
        uniqueById.set(item.id, item);
      });
      const deduped = Array.from(uniqueById.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
      setPracticeHistory(deduped);

      // Cache
      try {
        const serializable = deduped.map((h) => ({
          ...h,
          date: h.date?.toISOString?.() || h.date,
        }));
        localStorage.setItem(getHistoryCacheKey(userId), JSON.stringify({ data: serializable, updatedAt: Date.now() }));
      } catch (_) {
        // ignore cache write errors
      }
    } catch (err) {
      console.error('Error loading practice history:', err);
      setError('Failed to load practice history');
    }
  }, [userId]);

  // Calculate practice stats
  const calculateStats = useCallback(() => {
    const stats: PracticeStats = {
      totalGenerated: practiceContent.length,
      totalQuizzes: practiceContent.filter(c => c.type === 'quiz').length,
      totalQuestions: practiceContent.filter(c => c.type === 'likely_questions').length,
      totalFlashcards: practiceContent.filter(c => c.type === 'flashcards').length,
      averageScore: 0,
      totalAssessments: practiceContent.filter(c => c.assessmentScore !== undefined).length
    };

    // Calculate average score
    const scoredContent = practiceContent.filter(c => c.assessmentScore !== undefined);
    if (scoredContent.length > 0) {
      stats.averageScore = scoredContent.reduce((sum, c) => sum + (c.assessmentScore || 0), 0) / scoredContent.length;
    }

    setPracticeStats(stats);
  }, [practiceContent]);

  // Generate AI content
  const abortRef = { current: null as AbortController | null } as { current: AbortController | null };

  const generateContent = useCallback(async (
    type: 'quiz' | 'likely_questions' | 'flashcards',
    sourceContent: string,
    contentLength: number,
    source: 'text' | 'pdf' = 'text'
  ): Promise<PracticeContent | null> => {
    if (!userId || userId.trim() === '') {
      setError('User not authenticated');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Generate content using AI service
      console.log('Calling AI service with:', {
        type,
        sourceContent: sourceContent.substring(0, 100) + '...',
        contentLength,
        source
      });
      
      // Abort in-flight if any, then create a new controller
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch {}
      }
      abortRef.current = new AbortController();

      const aiContent = await aiService.generateContent(
        'practice',
        'practice_session',
        type === 'likely_questions' ? 'likely_questions' : type,
        sourceContent,
        contentLength,
        abortRef.current.signal
      );
      
      console.log('AI service response:', aiContent);

      if (!aiContent || !aiContent.content) {
        throw new Error('Failed to generate AI content');
      }

      // Normalize AI content to expected shapes
      const normalizeContent = (t: typeof type, raw: any): any => {
        if (t === 'quiz') {
          let questions = raw?.questions;
          if (!Array.isArray(questions)) {
            if (Array.isArray(raw)) questions = raw;
            else if (Array.isArray(raw?.quiz)) questions = raw.quiz;
            else if (Array.isArray(raw?.items)) questions = raw.items;
            else if (Array.isArray(raw?.data)) questions = raw.data;
          }
          questions = Array.isArray(questions) ? questions : [];
          // Coerce to expected fields
          const coerced = questions.slice(0, contentLength).map((q: any) => {
            const opts: string[] = Array.isArray(q?.options) ? q.options : [q?.a, q?.b, q?.c, q?.d].filter(Boolean);
            let correctIndex: number = typeof q?.correctAnswer === 'number' ? q.correctAnswer : -1;
            if (correctIndex === -1) {
              const ans = q?.answer || q?.correct || q?.correct_option || q?.solution;
              if (typeof ans === 'string') {
                const letter = ans.trim().toUpperCase();
                const letterIdx = { A: 0, B: 1, C: 2, D: 3 } as Record<string, number>;
                if (letterIdx[letter] !== undefined) correctIndex = letterIdx[letter];
                else {
                  const byText = opts.findIndex(o => typeof o === 'string' && o.toLowerCase() === ans.toLowerCase());
                  if (byText >= 0) correctIndex = byText;
                }
              }
            }
            return {
              question: q?.question || q?.prompt || q?.text || '',
              options: opts.length ? opts : ['Option A', 'Option B', 'Option C', 'Option D'],
              correctAnswer: Math.max(0, Math.min((opts.length ? opts.length - 1 : 3), correctIndex >= 0 ? correctIndex : 0)),
              explanation: q?.explanation || q?.why || ''
            };
          });
          return { questions: coerced };
        }
        if (t === 'likely_questions') {
          let questions = raw?.questions;
          if (!Array.isArray(questions)) {
            if (Array.isArray(raw)) questions = raw;
            else if (Array.isArray(raw?.items)) questions = raw.items;
          }
          questions = Array.isArray(questions) ? questions : [];
          const coerced = questions.slice(0, contentLength).map((q: any) => ({
            question: q?.question || q?.text || q?.prompt || '',
            options: Array.isArray(q?.options) ? q.options : undefined,
            correctAnswer: typeof q?.correctAnswer === 'number' ? q.correctAnswer : 0,
            explanation: q?.explanation || ''
          }));
          return { questions: coerced };
        }
        if (t === 'flashcards') {
          let cards = raw?.cards;
          if (!Array.isArray(cards)) {
            if (Array.isArray(raw)) cards = raw;
            else if (Array.isArray(raw?.items)) cards = raw.items;
          }
          cards = Array.isArray(cards) ? cards : [];
          const coerced = cards.slice(0, contentLength).map((c: any) => ({
            front: c?.front || c?.term || c?.question || '',
            back: c?.back || c?.definition || c?.answer || ''
          }));
          return { cards: coerced };
        }
        return raw;
      };

      const normalizedContent = normalizeContent(type, aiContent.content);

      // Create practice content object
      const practiceContentData: Omit<PracticeContent, 'id'> = {
        type,
        title: `${type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')} - ${new Date().toLocaleDateString()}`,
        content: normalizedContent,
        source,
        sourceContent: sourceContent.substring(0, 500) + (sourceContent.length > 500 ? '...' : ''), // Truncate for storage
        contentLength,
        createdAt: new Date(),
        userId,
        isBookmarked: false,
        metadata: {
          difficulty: aiContent.metadata?.difficulty || 'medium',
          estimatedTime: aiContent.metadata?.estimatedTime || 15,
          tags: ['AI-generated', 'practice', type]
        }
      };

      // Save to Firestore
      const docRef = await addDoc(practiceContentCollection, {
        ...practiceContentData,
        createdAt: Timestamp.fromDate(practiceContentData.createdAt)
      });

      const newContent: PracticeContent = {
        id: docRef.id,
        ...practiceContentData
      };

      // Update local state
      setPracticeContent(prev => [newContent, ...prev]);

      // Add to history
      const historyEntry: Omit<PracticeHistory, 'id'> = {
        title: newContent.title,
        type,
        date: new Date(),
        status: 'completed',
        userId
      };

      const historyDocRef = await addDoc(practiceHistoryCollection, {
        ...historyEntry,
        date: Timestamp.fromDate(historyEntry.date)
      });

      const newHistoryEntry: PracticeHistory = {
        id: historyDocRef.id,
        ...historyEntry
      };

      // Append to local state without introducing duplicates (by id)
      setPracticeHistory(prev => {
        const combined = [newHistoryEntry, ...prev];
        const uniqueById = new Map<string, PracticeHistory>();
        combined.forEach(item => {
          uniqueById.set(item.id, item);
        });
        return Array.from(uniqueById.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
      });

      return newContent;
    } catch (err) {
      console.error('Error generating content:', err);
      setError('Failed to generate content. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, practiceContentCollection, practiceHistoryCollection]);

  // Cleanup on unmount to cancel any pending AI requests
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch {}
      }
    };
  }, []);

  // Save content (bookmark)
  const saveContent = useCallback(async (contentId: string): Promise<void> => {
    try {
      const contentRef = doc(db, 'practiceContent', contentId);
      await updateDoc(contentRef, {
        isBookmarked: true
      });

      // Update local state
      setPracticeContent(prev => 
        prev.map(content => 
          content.id === contentId 
            ? { ...content, isBookmarked: true }
            : content
        )
      );
    } catch (err) {
      console.error('Error saving content:', err);
      setError('Failed to save content');
    }
  }, []);

  // Delete content
  const deleteContent = useCallback(async (contentId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'practiceContent', contentId));
      
      // Update local state
      setPracticeContent(prev => prev.filter(content => content.id !== contentId));
    } catch (err) {
      console.error('Error deleting content:', err);
      setError('Failed to delete content');
    }
  }, []);

  // Update assessment score
  const updateAssessmentScore = useCallback(async (
    contentId: string, 
    score: number
  ): Promise<void> => {
    try {
      const contentRef = doc(db, 'practiceContent', contentId);
      await updateDoc(contentRef, {
        assessmentScore: score,
        assessmentDate: Timestamp.fromDate(new Date())
      });

      // Update local state
      setPracticeContent(prev => 
        prev.map(content => 
          content.id === contentId 
            ? { ...content, assessmentScore: score, assessmentDate: new Date() }
            : content
        )
      );
    } catch (err) {
      console.error('Error updating assessment score:', err);
      setError('Failed to update assessment score');
    }
  }, []);

  // Get bookmarked content
  const getBookmarkedContent = useCallback((): PracticeContent[] => {
    return practiceContent.filter(content => content.isBookmarked);
  }, [practiceContent]);

  // Get content by type
  const getContentByType = useCallback((type: 'quiz' | 'likely_questions' | 'flashcards'): PracticeContent[] => {
    return practiceContent.filter(content => content.type === type);
  }, [practiceContent]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load data on component mount and user change
  useEffect(() => {
    if (userId && userId.trim() !== '') {
      loadPracticeContent();
      loadPracticeHistory();
    } else {
      // Reset loading state when no valid user
      setLoading(false);
    }
  }, [userId, loadPracticeContent, loadPracticeHistory]);

  // Recalculate stats when content changes
  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  return {
    // State
    loading,
    error,
    practiceContent,
    practiceHistory,
    practiceStats,

    // Actions
    generateContent,
    saveContent,
    deleteContent,
    updateAssessmentScore,
    loadPracticeContent,
    loadPracticeHistory,
    clearError,

    // Getters
    getBookmarkedContent,
    getContentByType
  };
};
